import React, { useContext, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import {
  BackIcon,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import { Link as RouterLink } from "react-router-dom"
import { DbgPaths } from "#/utils/Paths"
import { useTermsAndConditionsUrl } from "#/utils/hooks/legal-notice-urls"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { CarrotCardCostSharePreAuthContext } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"

export function PleaseConfirmTwoThingsObgynStep(): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const termsAndConditionsUrl = useTermsAndConditionsUrl()
  const { send, back } = useStateMachine()
  const [isPrimaryConfirmed, setIsPrimaryConfirmed] = useState(false)
  const [isSecondaryConfirmed, setIsSecondaryConfirmed] = useState(false)
  const {
    formState: { whatKindFertilityCare }
  } = useContext(CarrotCardCostSharePreAuthContext)

  const isAllConfirmed = isPrimaryConfirmed && isSecondaryConfirmed

  function handleContinue(): void {
    if (whatKindFertilityCare === JourneyTypes.IUI) {
      send("IUI")
    } else {
      send("")
    }
  }

  return (
    <Box>
      <Typography variant="h3" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xs })}>
        <FormattedMessage defaultMessage="Please confirm two things." />
      </Typography>

      <Stack spacing={theme.tokens.spacing.xl}>
        <Stack spacing={theme.tokens.spacing.md}>
          <Typography>
            <FormattedMessage
              defaultMessage="To be eligible, OB/GYN visits must be related to initial fertility testing and/or basic fertility interventions. If you haven't already, please review the <eligibleLink>eligible OB/GYN expenses</eligibleLink> in your benefit guide."
              values={{
                eligibleLink: (linkContent) => (
                  <Link
                    component={RouterLink}
                    to={DbgPaths.FERTILITY_COVERAGE}
                    color="inherit"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
          <Typography>
            <FormattedMessage
              defaultMessage="General OB/GYN visits not related to fertility are not covered. If you misrepresent the purpose of your visit, we may deny your expense. You may also lose access to Carrot for benefit misuse. For more information, please review <termsLink>our terms and conditions.</termsLink>"
              values={{
                termsLink: (linkContent) => (
                  <Link href={termsAndConditionsUrl} color="inherit" target="_blank" rel="noopener noreferrer">
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
          <Typography>
            <FormattedMessage defaultMessage="If you cannot check both boxes below, please do not submit this authorization." />
          </Typography>
        </Stack>

        <Stack spacing={theme.tokens.spacing.md}>
          <FormControlLabel
            control={
              <Checkbox checked={isPrimaryConfirmed} onChange={(e) => setIsPrimaryConfirmed(e.target.checked)} />
            }
            label={intl.formatMessage({
              defaultMessage: "This OB/GYN visit is related to eligible fertility services listed in my benefit guide."
            })}
          />
          <FormControlLabel
            control={
              <Checkbox checked={isSecondaryConfirmed} onChange={(e) => setIsSecondaryConfirmed(e.target.checked)} />
            }
            label={intl.formatMessage({
              defaultMessage:
                "This visit will not include a general wellness exam, annual exam, pregnancy care after 10 weeks of gestation, or any care unrelated to fertility."
            })}
          />
        </Stack>

        <Box
          sx={(theme) => ({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBlockStart: theme.tokens.spacing.md
          })}
        >
          <Button variant="text" color="secondary" onClick={back} startIcon={<BackIcon />}>
            <FormattedMessage defaultMessage="Back" />
          </Button>

          <Button variant="outlined" color="secondary" onClick={handleContinue} disabled={!isAllConfirmed}>
            <FormattedMessage defaultMessage="Continue" />
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}
